import { cn } from '@/lib/cn';
import { type ReactNode } from 'react';
import { useLazySection } from '@/components/settings/useLazySection';
import { Spinner } from '@/components/ui/Spinner';

type Props = {
  id?: string;
  title: string;
  description?: string;
  action?: ReactNode;
  lazy?: boolean;
  loading?: boolean;
  className?: string;
  children: ReactNode | ((visible: boolean) => ReactNode);
};

export function SettingsSection({
  id,
  title,
  description,
  action,
  lazy,
  loading,
  className,
  children
}: Props) {
  const { ref, visible } = useLazySection();
  const isVisible = !lazy || visible;
  const content = typeof children === 'function' ? children(isVisible) : children;

  return (
    <section
      id={id}
      ref={lazy ? (ref as React.RefObject<HTMLElement>) : undefined}
      className={cn(
        'scroll-mt-20 mt-6 rounded-[var(--fv-radius-lg)] border border-[var(--fv-border)] bg-[var(--fv-surface)] p-6 shadow-[var(--fv-shadow-sm)]',
        className
      )}
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <h2 className="text-base font-semibold text-[var(--fv-text)]">{title}</h2>
          {description ? (
            <p className="mt-1 text-sm text-[var(--fv-text-muted)]">{description}</p>
          ) : null}
        </div>
        {action ? <div className="shrink-0">{action}</div> : null}
      </div>
      <div className="mt-4 space-y-4">
        {loading ? (
          <div className="flex h-24 items-center justify-center">
            <Spinner size="md" />
          </div>
        ) : lazy && !isVisible ? (
          <div
            className="h-24 animate-pulse rounded-[var(--fv-radius)] bg-[var(--fv-bg)]"
            aria-hidden
          />
        ) : (
          content
        )}
      </div>
    </section>
  );
}
